import { resource, computed, effect, inject, signal } from '@angular/core';
import { patchState, signalStore, withMethods, withProps, withState } from '@ngrx/signals';
import { Router } from '@angular/router';
import { tap } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { LoginResponse } from '../models/user.model';
import { ConfigService } from '../services/config.service';
import { SnackbarService } from '../services/snackbar.service';
import { Variables, VarsDetails, SecretManager, ProjectSettings } from '../models/common.model';
import { Repository } from '../models/project.model';
import { normalizeRepoDetails, projectStore } from './project.store';
import { ErufunctionService } from '../services/erufunctions.service';


interface kmsResourceRequest {
  project_name?: string;
  canLoadKms?: boolean;
}

interface eventsResourceRequest {
  project_name?: string;
  canLoadEvents?: boolean;
}

export interface KM {
  id?: string;
  kms_store_type: string;
  region: string;
  kms_name: string;
  kms_desc: string;
  kms_alias: string;
  authentication: string;
  key: string;
  secret: string;
}

export interface AppEvent {
  id?: string;
  event_type: string;
  region: string;
  event_name: string;
  authentication: 'IAM' | 'SECRET';
  key?: string;
  secret?: string;
  fifo: boolean;
  tags: { name: string };
  msg_to_poll: number;
  wait_time: number;
  msg_visible_time_out: number;
}

type requestHeader = {
  idTokenKey: string;
  idToken: string;
  key: string;
  value: string;
  baseUrl: string;
  userId: string;
  serviceAccess: string;
};

type configState = {
  requestHeader: requestHeader | undefined;
  requestParams: { project_name: string };
  kmsFormData: KM[];
};

const initialState: configState = {
  requestHeader: undefined,
  requestParams: { project_name: '' },
  kmsFormData: []
};

// Fields each service's ProjectSettings struct accepts (backends use DisallowUnknownFields)
const PROJECT_SETTINGS_FIELDS: Record<string, string[]> = {
  eruauth: ['claims_key', 'kid_key'],
  erufunctions: ['claims_key', 'async_repoll_wait_time'],
  eruql: ['claims_key', 'derived_field_limits'],
  eruai: ['claims_key', 'tool_catalog_access'],
  erufiles: ['claims_key'],
  erugateway: ['claims_key'],
};

interface EventsResponse {
  events: { [key: string]: AppEvent } | AppEvent[];
}

interface Tenant {
  tenant_id: string;
  models: Record<string, any>;
}

interface TenantVariables {
  [project: string]: {
    vars: Record<string, VarsDetails> | null;
    env_vars: Record<string, VarsDetails> | null;
    secrets: Record<string, VarsDetails> | null;
  };
}

export const configStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps((store) => ({
    _router: inject(Router),
    _configService: inject(ConfigService),
    _projectStore: inject(projectStore),
    _erufuncionsService: inject(ErufunctionService),
    _snackbarService: inject(SnackbarService),
    _connectError: signal<string>(''),
    _currentProject: signal<string>(''),
    _currentEnv: signal<string>(''),
    _currentService: signal<string>(''),
    _canLoadConfig: signal<boolean>(false),
    _mainLoaded: signal<boolean>(false),
    _variables: signal<Variables>({ vars: new Map(), env_vars: new Map(), secrets: new Map() }),
    _kmsResourceRequest: signal<kmsResourceRequest>({
      project_name: '',
      canLoadKms: false
    }),
    _eventsResourceRequest: signal<eventsResourceRequest>({
      project_name: '',
      canLoadEvents: false
    }),
    _secret_manager: signal<SecretManager>({
      sm_store_type: '',
      cache_store_type: '',
      region: '',
      sm_name: '',
      authentication: '',
      key: '',
      secret: ''
    }),
    _project_settings: signal<ProjectSettings>({
      claims_key: '',
      kid_key: ''
    }),
    _kms: signal<KM[]>([]),
    _events: signal<AppEvent[]>([]),
    _tenant_secret_manager: signal<SecretManager | null>(null),
    _tenant_variables: signal<TenantVariables>({}),
    _tenants: signal<Record<string, Tenant>>({})
  })),
  withProps((store) => ({
    setKmsResourceRequest: (project_name: string, canLoadKms: boolean) => {
      store._kmsResourceRequest.set({ project_name, canLoadKms });
    },
    setEventsResourceRequest: (project_name: string, canLoadEvents: boolean) => {
      store._eventsResourceRequest.set({ project_name, canLoadEvents });
    },
    currentProject: computed(() => store._currentProject()),
    currentEnv: computed(() => store._currentEnv()),
    currentService: computed(() => store._currentService()),
    canLoadConfig: computed(() => store._canLoadConfig()),
    mainLoaded: computed(() => store._mainLoaded()),
    connectError: computed(() => store._connectError()),
    variables: computed(() => store._variables()),
    secret_manager: computed(() => store._secret_manager()),
    project_settings: computed(() => store._project_settings()),
    tenant_secret_manager: computed(() => store._tenant_secret_manager()),
    tenant_variables: computed(() => store._tenant_variables()),
    tenants: computed(() => store._tenants()),
    baseUrl: computed(() => store.requestHeader()?.baseUrl || ''),
    repositories: computed(() => {
      const projectName = store._currentProject();
      const envName = store._currentEnv();
      if (!projectName || !envName) return [];
      const project = store._projectStore.projects()?.find(p => p.project_name === projectName);
      if (!project) return [];
      const env = project.public___project_envs?.find(e => e.env_name === envName);
      const repo = normalizeRepoDetails(env?.repo_details);
      return repo ? [repo] : [];
    }),
    kms: computed(() => store._kms()),
    events: computed(() => store._events())
  })),
  withMethods((store) => {
    const fetchEvents = async (baseUrl: string, projectName: string) => {
      console.log(`configStore: Fetching events for ${projectName} from ${baseUrl}`);
      try {
        const response = await firstValueFrom(
          store._configService.fetchEvents(baseUrl, projectName)
        ) as EventsResponse;
        console.log(`configStore: Raw fetchEvents response:`, response);
        const eventsArray = Array.isArray(response.events)
          ? response.events
          : Object.values(response.events || {}).map(event => ({
              ...event,
              id: event.event_name
            }));
        console.log(`configStore: Processed events array:`, eventsArray);
        store._events.set(eventsArray);
        console.log(`configStore: Events signal updated:`, store._events());
        return eventsArray;
      } catch (error) {
        console.error(`configStore: Failed to fetch events:`, error);
        store._events.set([]);
        return [];
      }
    };

    return {
      updateBaseUrl: (baseUrl: string | undefined, serviceAccess: string | undefined) => {
        const currentHeader = store.requestHeader();
        patchState(store, {
          requestHeader: {
            idTokenKey: currentHeader?.idTokenKey || '',
            idToken: currentHeader?.idToken || '',
            key: currentHeader?.key || '',
            value: currentHeader?.value || '',
            baseUrl: baseUrl || '',
            userId: currentHeader?.userId || '',
            serviceAccess: serviceAccess || ''
          },
        });
      },
      setMainLoaded: (value: boolean) => {
        store._mainLoaded.set(value);
      },
      setVariables: (variablesFromConfig: Variables) => {
        store._variables.set(variablesFromConfig);
      },
      setCanLoadConfig: (value: boolean) => {
        store._canLoadConfig.set(value);
      },
      setCurrentProjectEnv: (project: string, env: string) => {
        store._currentProject.set(project);
        store._currentEnv.set(env);
      },
      setCurrentProject: (project: string) => {
        store._currentProject.set(project);
      },
      setCurrentEnv: (env: string) => {
        store._currentEnv.set(env);
      },
      setCurrentService: (service: string) => {
        store._currentService.set(service);
      },
      setSecretManager: (secretManager: SecretManager) => {
        store._secret_manager.set(secretManager);
      },
      setProjectSettings: (projectSettings: ProjectSettings) => {
        store._project_settings.set(projectSettings);
      },
      setConnectError: (message: string) => {
        store._connectError.set(message);
      },
      // service is the route segment (eruauth, eruql, ...); gateway settings are global
      saveProjectSettings: async (service: string, projectSettings: ProjectSettings) => {
        const fields = PROJECT_SETTINGS_FIELDS[service];
        if (!fields) {
          store._snackbarService.error(`Settings are not supported for ${service}`);
          return false;
        }
        const merged: Record<string, any> = { ...store._project_settings(), ...projectSettings };
        const payload: Record<string, any> = {};
        fields.forEach((field) => {
          if (merged[field] !== undefined && merged[field] !== null) {
            payload[field] = merged[field];
          }
        });
        if (service === 'eruauth' && !payload['kid_key']) {
          payload['kid_key'] = '';
        }
        try {
          await firstValueFrom(
            store._configService.saveProjectSettings(
              store.requestHeader()?.baseUrl || '',
              service === 'erugateway' ? null : store.requestParams().project_name,
              payload
            )
          );
          store._project_settings.set({ ...merged, ...payload } as ProjectSettings);
          store._snackbarService.success('Project settings saved');
          return true;
        } catch (error: any) {
          console.error('configStore: Failed to save project settings:', error);
          store._snackbarService.error(error?.message || 'Failed to save project settings');
          return false;
        }
      },
      // service is the route segment; gateway keeps its secret manager under project "gateway"
      saveSecretManager: async (service: string, secretManager: SecretManager) => {
        const smType = (secretManager.sm_store_type || '').toUpperCase();
        if (smType !== 'AWS' && smType !== 'GCP') {
          store._snackbarService.error('Store type must be AWS or GCP');
          return false;
        }
        const payload: Record<string, any> = {
          sm_store_type: smType,
          cache_store_type: secretManager.cache_store_type || 'INMEMORY',
          sm_name: secretManager.sm_name || '',
        };
        if (smType === 'AWS') {
          const authentication = (secretManager.authentication || '').toUpperCase();
          payload['region'] = secretManager.region || '';
          payload['authentication'] = authentication;
          payload['key'] = authentication === 'SECRET' ? secretManager.key || '' : '';
          payload['secret'] = authentication === 'SECRET' ? secretManager.secret || '' : '';
        }
        const projectName = service === 'erugateway' ? 'gateway' : store.requestParams().project_name;
        try {
          await firstValueFrom(
            store._configService.saveSecretManager(store.requestHeader()?.baseUrl || '', projectName, smType, payload)
          );
          store._secret_manager.set({ ...secretManager, ...payload } as SecretManager);
          store._snackbarService.success('Secret manager saved');
          return true;
        } catch (error: any) {
          console.error('configStore: Failed to save secret manager:', error);
          store._snackbarService.error(error?.message || 'Failed to save secret manager');
          return false;
        }
      },
      setKms: (kms: KM[]) => {
        store._kms.set(kms);
      },
      setEvents: (events: AppEvent[]) => {
        store._events.set(events);
      },
      setTenantSecretManager: (tenantSecretManager: SecretManager | null) => {
        store._tenant_secret_manager.set(tenantSecretManager);
      },
      setTenantVariables: (tenantVariables: TenantVariables) => {
        store._tenant_variables.set(tenantVariables);
      },
      setTenants: (tenants: Record<string, Tenant>) => {
        store._tenants.set(tenants);
      },
      refreshSecrets() {
        store._configService.refreshSecrets(
          store.requestHeader()?.baseUrl || '',
          store.requestParams().project_name
        ).subscribe({
          next: (response) => {
        console.log(`configStore: Secrets refreshed successfully`, response);
        const currentSecretManager = store._secret_manager();
        const updatedSecretManager = { ...currentSecretManager, ...response };
        store._secret_manager.set(updatedSecretManager);
        // Show success snackbar
        //store._configService.showSnackbar('Secrets refreshed successfully', 'success');
          },
          error: (error) => {
        console.error(`configStore: Failed to refresh secrets:`, error);
        // Show error snackbar
        //store._configService.showSnackbar('Failed to refresh secrets', 'error');
          }
        });
      },
      loadProjectConfig: async (projectName: string) => {
        console.log(`configStore: Loading project config for ${projectName}`);
        try {
          const baseUrl = store.requestHeader()?.baseUrl || '';

          // Fetch KMS and Events
          await fetchEvents(baseUrl, projectName);
          const kmsResponse = await firstValueFrom(store._configService.fetchKms(baseUrl, projectName));
          if (kmsResponse && kmsResponse.kms) {
            const kmsArray = Array.isArray(kmsResponse.kms)
              ? kmsResponse.kms
              : Object.entries(kmsResponse.kms).map(([id, data]) => ({ id, ...(data as any) }));
            store._kms.set(kmsArray);
          }

          // Construct tenant variables and tenants (sample data, adjust based on actual source)
          const tenantVariables: TenantVariables = {
            [projectName]: {
              vars: null,
              env_vars: null,
              secrets: {
                anthropic_api_key: { key: "anthropic_api_key" },
              },
            },
          };

          const tenants: Record<string, Tenant> = {
            d1: {
              tenant_id: "d1",
              models: {},
            },
            [projectName]: {
              tenant_id: projectName,
              models: {},
            },
          };

          // Update signals
          store._tenant_secret_manager.set(null); // Adjust if tenant_secret_manager is available
          store._tenant_variables.set(tenantVariables);
          store._tenants.set(tenants);

        } catch (error) {
          console.error(`configStore: Failed to load project config:`, error);
        }
      },
      saveKMS: (kms: KM) => {
        store._configService
          .saveKms(
            store.requestHeader()?.baseUrl || '',
            store.requestParams().project_name,
            kms
          )
          .subscribe({
            next: (response) => {
              console.log(`configStore: KMS ${kms.kms_name || 'new'} saved successfully`, response);
              const currentKms = store._kms();
              const savedKms = { ...kms, kms_name: response.kms_name || kms.kms_name || 'temp-' + Date.now() };
              const kmsIndex = currentKms.findIndex(km => km.kms_name === savedKms.kms_name);
              let updatedKms: KM[];
              if (kmsIndex >= 0) {
                updatedKms = [...currentKms];
                updatedKms[kmsIndex] = savedKms;
              } else {
                updatedKms = [...currentKms, savedKms];
              }
              store._kms.set(updatedKms);
            },
            error: (error) => {
              console.error(`configStore: Failed to save KMS ${kms.kms_name || 'new'}:`, error);
            }
          });
      },
      deleteKMS: (kms_name: string) => {
        store._configService
          .deleteKMS(
            store.requestHeader()?.baseUrl || '',
            store.requestParams().project_name,
            kms_name
          )
          .subscribe({
            next: () => {
              const currentKms = store._kms();
              const updatedKms = currentKms.filter(km => km.kms_name !== kms_name);
              store._kms.set(updatedKms);
            },
            error: (error) => {
              console.error(`configStore: Failed to delete KMS ${kms_name}:`, error);
            }
          });
      },
      saveEvent: (event: AppEvent) => {
        store._configService
          .saveEvents(
            store.requestHeader()?.baseUrl || '',
            store.requestParams().project_name,
            event
          )
          .subscribe({
            next: (response) => {
              const currentEvents = store._events();
              const savedEvent = { ...event, id: response.id || event.id || event.event_name };
              const eventIndex = currentEvents.findIndex((ev: AppEvent) => ev.id === savedEvent.id);
              let updatedEvents: AppEvent[];
              if (eventIndex >= 0) {
                updatedEvents = [...currentEvents];
                updatedEvents[eventIndex] = savedEvent;
              } else {
                updatedEvents = [...currentEvents, savedEvent];
              }
              store._events.set(updatedEvents);
              alert('Event saved successfully');
            },
            error: (error) => {
              alert('Failed to save event');
            }
          });
      },
      deleteEvent: (id: string) => {
        console.log(`configStore: Initiating deletion of event with id: ${id}`);
        store._configService
          .deleteEvent(
            store.requestHeader()?.baseUrl || '',
            store.requestParams().project_name,
            id
          )
          .subscribe({
            next: () => {
              console.log(`configStore: Event ${id} deleted successfully`);
              const currentEvents = store._events();
              const updatedEvents = currentEvents.filter(ev => ev.id !== id && ev.event_name !== id);
              store._events.set(updatedEvents);
              console.log(`configStore: Updated events:`, updatedEvents);
            },
            error: (error) => {
              console.error(`configStore: Failed to delete event ${id}:`, error);
            }
          });
      },
      fetchEvents,
      saveVariables: (baseUrl: string, projectName: string, variable: VarsDetails, varType: string) => {
        console.log(`configStore: Saving variable ${variable.key} as ${varType}`);
        return store._configService
          .saveVariables(baseUrl, projectName, variable, varType)
          .pipe(
            tap({
              next: () => {
                console.log(`configStore: Variable ${variable.key} saved successfully`);
                const currentVars = store._variables();
                
                // Safely convert vars, env_vars, secrets to Maps
                const varsMap = currentVars.vars instanceof Map
                  ? currentVars.vars
                  : currentVars.vars && typeof currentVars.vars === 'object' && currentVars.vars !== null
                    ? new Map(Object.entries(currentVars.vars).map(([k, v]) => [k, v as VarsDetails]))
                    : new Map();
                    
                const envVarsMap = currentVars.env_vars instanceof Map
                  ? currentVars.env_vars
                  : currentVars.env_vars && typeof currentVars.env_vars === 'object' && currentVars.env_vars !== null
                    ? new Map(Object.entries(currentVars.env_vars).map(([k, v]) => [k, v as VarsDetails]))
                    : new Map();
                    
                const secretsMap = currentVars.secrets instanceof Map
                  ? currentVars.secrets
                  : currentVars.secrets && typeof currentVars.secrets === 'object' && currentVars.secrets !== null
                    ? new Map(Object.entries(currentVars.secrets).map(([k, v]) => [k, v as VarsDetails]))
                    : new Map();
                
                const newVars: Variables = {
                  vars: varsMap,
                  env_vars: envVarsMap,
                  secrets: secretsMap,
                };
                
                switch (varType) {
                  case 'var':
                    newVars.vars.set(variable.key, variable);
                    break;
                  case 'envvar':
                    newVars.env_vars.set(variable.key, variable);
                    break;
                  case 'secret':
                    newVars.secrets.set(variable.key, variable);
                    break;
                }
                
                store._variables.set(newVars);
                console.log(`configStore: Variables updated:`, newVars);
              },
              error: (error) => {
                console.error(`configStore: Failed to save variable ${variable.key}:`, error);
                // We're returning the error through the Observable rather than handling it here
              }
            })
          );
      },
      // Inside configStore.ts - update the deleteVariables method to return an Observable
deleteVariables: (baseUrl: string, projectName: string, variable: VarsDetails, varType: string) => {
  console.log(`configStore: Deleting variable ${variable.key} as ${varType}`);
  return store._configService
    .deleteVariables(baseUrl, projectName, variable, varType)
    .pipe(
      tap({
        next: () => {
          console.log(`configStore: Variable ${variable.key} deleted successfully`);
          const currentVars = store._variables();
          
          // Safely convert vars, env_vars, secrets to Maps
          const varsMap = currentVars.vars instanceof Map
            ? currentVars.vars
            : currentVars.vars && typeof currentVars.vars === 'object' && currentVars.vars !== null
              ? new Map(Object.entries(currentVars.vars).map(([k, v]) => [k, v as VarsDetails]))
              : new Map();
              
          const envVarsMap = currentVars.env_vars instanceof Map
            ? currentVars.env_vars
            : currentVars.env_vars && typeof currentVars.env_vars === 'object' && currentVars.env_vars !== null
              ? new Map(Object.entries(currentVars.env_vars).map(([k, v]) => [k, v as VarsDetails]))
              : new Map();
              
          const secretsMap = currentVars.secrets instanceof Map
            ? currentVars.secrets
            : currentVars.secrets && typeof currentVars.secrets === 'object' && currentVars.secrets !== null
              ? new Map(Object.entries(currentVars.secrets).map(([k, v]) => [k, v as VarsDetails]))
              : new Map();
          
          const newVars: Variables = {
            vars: varsMap,
            env_vars: envVarsMap,
            secrets: secretsMap,
          };
          
          switch (varType) {
            case 'var':
              newVars.vars.delete(variable.key);
              break;
            case 'envvar':
              newVars.env_vars.delete(variable.key);
              break;
            case 'secret':
              newVars.secrets.delete(variable.key);
              break;
          }
          
          store._variables.set(newVars);
          console.log(`configStore: Variables updated:`, newVars);
        },
        error: (error) => {
          console.error(`configStore: Failed to delete variable ${variable.key}:`, error);
          // We're returning the error through the Observable rather than handling it here
        }
      })
    );
},
      updateProjectName: (project_name: string | undefined) => {
        patchState(store, {
          requestParams: {
            project_name: project_name || '',
          },
        });
      },
      // Logs this tab into its instance; returns false (and records/shows the error) on failure
      async LoginWithUserPassword(url: string, user_name: string, password: string, headerKey: string, responseKey: string): Promise<boolean> {
        try {
          const auth = await store._configService.loginForAuth(url, user_name, password, headerKey, responseKey);
          patchState(store, {
            requestHeader: {
              ...auth,
              baseUrl: store.requestHeader()?.baseUrl || '',
              serviceAccess: store.requestHeader()?.serviceAccess || ''
            },
          });
          store._connectError.set('');
          store._canLoadConfig.set(true);
          if (store.currentProject() && !store._projectStore.projects()?.length) {
            await store._projectStore.fetchProjects(store.currentProject(), store.currentEnv());
          }
          return true;
        } catch (error: any) {
          console.error('Login error:', error);
          const message = `Login to ${store.currentEnv() || 'environment'} failed: ${error?.message || 'unknown error'}`;
          store._connectError.set(message);
          store._snackbarService.error(message);
          return false;
        }
      },
      // API-key envs: no login call, the key is sent as a static header
      setApiKeyHeader(headerKey: string, headerValue: string) {
        patchState(store, {
          requestHeader: {
            key: headerKey,
            value: headerValue,
            idTokenKey: 'id_token',
            idToken: '',
            baseUrl: store.requestHeader()?.baseUrl || '',
            userId: '',
            serviceAccess: store.requestHeader()?.serviceAccess || ''
          },
        });
      },
      fetchVars: computed(() => {
        const varsMap = store.variables().vars;
        if (!varsMap) return [];
        let vars: VarsDetails[] = [];
        if (varsMap instanceof Map) {
          vars = Array.from(varsMap.values());
        } else if (typeof varsMap === 'object') {
          vars = Object.values(varsMap);
        }
        return vars;
      }),
      fetchEnvVars: computed(() => {
        const envVarsMap = store.variables().env_vars;
        if (!envVarsMap) return [];
        let envVars: VarsDetails[] = [];
        if (envVarsMap instanceof Map) {
          envVars = Array.from(envVarsMap.values());
        } else if (typeof envVarsMap === 'object') {
          envVars = Object.values(envVarsMap);
        }
        return envVars;
      }),
      fetchSecrets: computed(() => {
        const secretsMap = store.variables().secrets;
        if (!secretsMap) return [];
        let secrets: VarsDetails[] = [];
        if (secretsMap instanceof Map) {
          secrets = Array.from(secretsMap.values());
        } else if (typeof secretsMap === 'object') {
          secrets = Object.values(secretsMap);
        }
        return secrets;
      }),
      fetchSecretManager: computed(() => {
        const secretManager = store._secret_manager();
        if (!secretManager) return [];
        let secretManagers: SecretManager[] = [];
        if (secretManager instanceof Map) {
          secretManagers = Array.from(secretManager.values());
        } else if (typeof secretManager === 'object') {
          secretManagers = Object.values(secretManager);
        }
        return secretManagers;
      }),
      fetchProjectSettings: computed(() => {
        const projectSettings = store._project_settings();
        if (!projectSettings) return [];
        let projectSettingsArray: ProjectSettings[] = [];
        if (projectSettings instanceof Map) {
          projectSettingsArray = Array.from(projectSettings.values());
        } else if (typeof projectSettings === 'object') {
          projectSettingsArray = Object.values(projectSettings);
        }
        return projectSettingsArray;
      }),
      fetchkms: computed(() => {
        const kms = store._kms();
        if (!kms) return [];
        let kmsArray: KM[] = [];
        if (kms instanceof Map) {
          kmsArray = Array.from(kms.values());
        } else if (typeof kms === 'object') {
          kmsArray = Object.values(kms);
        }
        return kmsArray;
      })
    };
  }),
  withProps((store) => ({
    eruKmsResource: resource<KM[], kmsResourceRequest>({
      params: () => store._kmsResourceRequest(),
      loader: async ({ params: request }) => {
        try {
          const baseUrl = store.requestHeader()?.baseUrl || '';
          if (!request.canLoadKms) {
            return [];
          }
          const projectName = request.project_name;
          if (!projectName) {
            return [];
          }
          const response = await firstValueFrom(
            store._configService.fetchKms(baseUrl, projectName)
          );
          if (response && response.kms) {
            let kmsArray = response.kms;
            if (response.kms && typeof response.kms === 'object' && !Array.isArray(response.kms)) {
              kmsArray = Object.entries(response.kms).map(([id, data]) => ({
                id,
                ...(data as any)
              }));
            }
            store._kms.set(kmsArray);
            return kmsArray;
          } else {
            return [];
          }
        } catch (error) {
          console.error('Error loading KMS:', error);
          return [];
        }
      }
    }),
    eruEventsResource: resource<AppEvent[], eventsResourceRequest>({
      params: () => store._eventsResourceRequest(),
      loader: async ({ params: request }) => {
        try {
          const baseUrl = store.requestHeader()?.baseUrl || '';
          if (!request.canLoadEvents) {
            return [];
          }
          const projectName = request.project_name;
          if (!projectName) {
            console.log('Missing project_name for Events fetch');
            return [];
          }
          const response = await firstValueFrom(
            store._configService.fetchEvents(baseUrl, projectName)
          );
          if (response && response.events) {
            const eventsArray: AppEvent[] = Array.isArray(response.events)
              ? response.events
              : Object.values(response.events || {});
            store._events.set(eventsArray);
            return eventsArray;
          } else {
            return [];
          }
        } catch (error) {
          console.log('Error loading Events:', error);
          store._events.set([]);
          return [];
        }
      }
    })
  }))
);